import type { Catalog } from '../types';

/**
 * O perfil: identidade, a explicação do WHS, preferências e privacidade.
 *
 * É aqui que mora o seletor de idioma — e por isso é a única tela em que a troca de idioma é
 * possível, com recarga da página. Ver `lib/i18n/locale.ts` para o porquê.
 */
export const profile = {
  'profile.gender.M': { pt: 'Masculino', en: 'Male' },
  'profile.gender.F': { pt: 'Feminino', en: 'Female' },
  'profile.gender.X': { pt: 'Não informado', en: 'Not stated' },

  /** Como você entra: senha própria, ou a conta de um provedor. */
  'profile.provider.local': { pt: 'Senha', en: 'Password' },
  'profile.provider.google': { pt: 'Google', en: 'Google' },
  'profile.provider.apple': { pt: 'Apple', en: 'Apple' },

  'profile.updated': { pt: 'Perfil atualizado', en: 'Profile updated' },
  'profile.sharingOn': {
    pt: 'Suas voltas voltam para a timeline',
    en: 'Your rounds are back on the timeline',
  },
  'profile.sharingOff': {
    pt: 'Suas voltas saíram da timeline',
    en: 'Your rounds left the timeline',
  },

  'profile.edit': { pt: 'Editar perfil', en: 'Edit profile' },
  'profile.memberSince': {
    pt: 'Membro desde {year} · {email}',
    en: 'Member since {year} · {email}',
  },

  'profile.notEnoughRounds': { pt: 'Sem voltas suficientes', en: 'Not enough rounds' },
  'profile.handicapMeta': { pt: '{base} · {date}', en: '{base} · {date}' },
  'profile.recalculate': { pt: 'Recalcular', en: 'Recalculate' },
  'profile.howCalculated': { pt: 'Como o índice é calculado', en: 'How the index is calculated' },
  'profile.explainerStrong': {
    pt: '8 menores differentials dos últimos 20',
    en: 'lowest 8 differentials of the last 20',
  },
  'profile.explainer': {
    pt: 'O índice usa os {strong}. Com menos de 20 voltas, a regra WHS usa menos scores e aplica um ajuste:',
    en: 'The index uses the {strong}. Below 20 rounds, the WHS rule uses fewer scores and applies an adjustment:',
  },
  'profile.yourBand': {
    pt: 'Você está na faixa de {band}: o índice sai {base}.',
    en: 'You are in the {band} band: the index comes {base}.',
  },
  'profile.baseLowest': { pt: 'do menor differential', en: 'from the lowest differential' },
  'profile.baseAverage': {
    pt: 'da média dos {count} menores differentials',
    en: 'from the average of the {count} lowest differentials',
  },

  /** As faixas da tabela WHS, ditas em texto. "20+" é a última e não tem limite superior. */
  'profile.band20plus': { pt: '20+ voltas', en: '20+ rounds' },
  'profile.bandRange': { pt: '{from}–{to} voltas', en: '{from}–{to} rounds' },
  'profile.bandExact': {
    pt: { one: '{count} volta', other: '{count} voltas' },
    en: { one: '{count} round', other: '{count} rounds' },
  },
  'profile.tableRounds': { pt: 'Voltas', en: 'Rounds' },
  'profile.tableBase': { pt: 'Base', en: 'Base' },
  'profile.tableAdjust': { pt: 'Ajuste', en: 'Adjustment' },
  'profile.tableLowest1': { pt: 'menor 1', en: 'lowest 1' },
  'profile.tableAverageOf': { pt: 'média dos {count} menores', en: 'average of the {count} lowest' },

  'profile.myData': { pt: 'Meus dados', en: 'My details' },
  'profile.displayName': { pt: 'Nome de exibição', en: 'Display name' },
  'profile.genderLabel': { pt: 'Gênero', en: 'Gender' },
  'profile.genderHint': {
    pt: 'afeta o rating do tee e o handicap',
    en: 'affects the tee rating and the handicap',
  },
  'profile.homeCourse': { pt: 'Meu campo', en: 'My course' },
  'profile.none': { pt: 'Nenhum', en: 'None' },

  'profile.preferences': { pt: 'Preferências', en: 'Preferences' },
  'profile.distanceUnit': { pt: 'Unidade de distância', en: 'Distance unit' },
  'profile.appliesEverywhere': { pt: 'vale para todas as telas', en: 'applies to every screen' },
  'profile.yards': { pt: 'Jardas', en: 'Yards' },
  'profile.meters': { pt: 'Metros', en: 'Meters' },

  'profile.language': { pt: 'Idioma', en: 'Language' },
  /**
   * Cada idioma é nomeado **no próprio idioma** — quem abre o app em inglês por engano precisa
   * reconhecer "Português" para voltar, e "Portuguese" não ajudaria nisso.
   */
  'profile.language.pt': { pt: 'Português', en: 'Português' },
  'profile.language.en': { pt: 'English', en: 'English' },
  'profile.languageHint': {
    pt: 'a página recarrega ao trocar',
    en: 'the page reloads when you switch',
  },

  'profile.changePassword': { pt: 'Alterar senha', en: 'Change password' },
  'profile.soon': { pt: 'Em breve', en: 'Coming soon' },

  'profile.privacy': { pt: 'Privacidade', en: 'Privacy' },
  'profile.shareRounds': {
    pt: 'Minhas voltas na timeline dos amigos',
    en: 'My rounds on my friends’ timeline',
  },
  'profile.shareRoundsHint': {
    pt: 'Ser amigo significa compartilhar. Desligar tira você da timeline deles e eles da sua.',
    en: 'Being friends means sharing. Turning it off takes you off their timeline and them off yours.',
  },
  'profile.on': { pt: 'Ligado', en: 'On' },
  'profile.off': { pt: 'Desligado', en: 'Off' },

  'profile.logout': { pt: 'Sair', en: 'Log out' },
  'profile.deleteAccount': { pt: 'Excluir minha conta', en: 'Delete my account' },
  'profile.logoutTitle': { pt: 'Sair da conta?', en: 'Log out?' },
  'profile.logoutBody': {
    pt: 'Suas voltas continuam salvas. É só entrar de novo.',
    en: 'Your rounds stay saved. Just log back in.',
  },

  'profile.save': { pt: 'Salvar', en: 'Save' },
  'profile.nameHint': { pt: '1 a 80 caracteres', en: '1 to 80 characters' },
} as const satisfies Catalog;
